import styles from "./Navbar.module.css";
import catermatLogo from "../../../assets/images/catermatLogo.svg";
import { useState } from "react";
import { Menu } from "lucide-react";
import MarikaCropped from "../../../assets/images/MarikaCropped.png";
import AddressInput from "../../Input/AddressInput/AddressInput";
import DatePicker from "../../DatePicker/DatePicker";
import CartButton from "../../Button/CartButton/CartButton";
import UserButton from "../../Button/UserButton/UserButton";
import { Link } from "react-router-dom";
import MenuBlock from "../Menu/Menu";
import OrderSettingsPopup from "../../Popups/OrderSettings/OrderSettings";
import OnboardingPopup from "../../Popups/Onboarding/Onboarding";
import { usePreferences } from "../../../context/PreferencesContext";
import { useUserContext } from "../../../context/UserContext";
import { useWindowSize } from "../../../hooks/useWindowSize";
import { useOrderContext } from "../../../context/OrderContext";
import { useCartContext } from "../../../context/CartContext";
import { useOnboardingData } from "../../../hooks/useOnboardingData";
import { useTranslation } from "react-i18next";
import SearchButton from "../../Button/SearchButton/SearchButton";
import { usePublicCatalog } from "../../../context/PublicCatalogContext";
import { formatDate, normalizeDate, filterDatesOnOrAfterEffectiveTomorrow } from "../../../utils/calendar";
import { useProducts } from "../../../context/ProductContext";
import { useStorageItems } from "../../../context/StorageItemsContext";
import { useDeliveryAddresses } from "../../../hooks/useDeliveryAddresses";
import { useCartPriceSummary } from "../../../hooks/useCartPriceSummary";
import { useUpdateCartHandler } from "../../../hooks/useUpdateCartHandler";

/**
 * Navbar - home version.
 *
 * @returns TSX.Element
 */
const Navbar = () => {
    const { t } = useTranslation(); // Translation
    const { isPublicView } = usePublicCatalog(); // Public View Flag
    const { cartOpen, closeCart, openCart, cartData, cartLoaded } = useCartContext(); // Cart data
    const { clearData } = useOnboardingData(); // Onboarding data
    const { orderData, handleOrder } = useOrderContext(); // Order data - for setting address
    const { loggedIn, logout, userData } = useUserContext(); // User data, auth flag
    const screenWidth = useWindowSize(); // Screen width
    const { selectedCurrency } = usePreferences(); // Current selected currency

    const { allProducts, packages } = useProducts();
    const { items } = useStorageItems();

    const [menuOpen, setMenuOpen] = useState<boolean>(false); // Handle toggling burger menu
    const [orderSettingsOpen, setOrderSettingsOpen] = useState<boolean>(false); // Handle toggling order settings popup
    const [onboardingOpen, setOnboardingOpen] = useState<boolean>(false); // Onboarding popup open flag

    // Use consolidated hooks
    const { indicatorText } = useCartPriceSummary({
        cartData,
        selectedCurrency,
        allProducts,
        packages,
        items,
    });

    const adresses = useDeliveryAddresses(cartData?.subscriber_id, userData);
    const { handleUpdateCart } = useUpdateCartHandler();

    const cartDatesISO = filterDatesOnOrAfterEffectiveTomorrow(
        (cartData?.selected_dates ?? []).map(normalizeDate).filter(Boolean)
    );

    // Change order address
    const handleAdressChange = (adress: string) => {
        if (cartLoaded && cartData && cartDatesISO.length > 0) {
            handleUpdateCart(cartDatesISO, adress);
            handleOrder("address", adress);
            handleOrder("date", cartDatesISO);
        }
    };
    // Change order dates
    const handleDateChange = (date: string[]) => {
        if (cartLoaded && cartData && date.length !== 0) {
            // Make sure user isnt able to deselect all dates
            const isoDates = filterDatesOnOrAfterEffectiveTomorrow(date.map(normalizeDate));
            if (isoDates.length === 0) return;

            handleUpdateCart(isoDates);
            handleOrder("date", isoDates);
        }
    };

  return (
        <>
        <MenuBlock display={menuOpen} handleDisplay={setMenuOpen} version="default" />
        <div className={styles.Navbar}>
            {screenWidth > 940 ? (
            <>
                <div className={styles.NavbarMain}>
                <div className={styles.LogoWrap}>
                    {!isPublicView ? (
                    <Link to={"/home"}>
                        <img className={styles.Logo} src={catermatLogo} alt="Catermat Logo" />
                    </Link>
                    ) : (
                    <img className={styles.Logo} src={catermatLogo} alt="Catermat Logo" />
                    )}
                    <p>
                    {t("navbar.for")}
                    <span>
                        {" " +
                        userData.subscribers?.find(
                            (sub) => sub.subscriber_id === cartData?.subscriber_id
                        )?.name || ""}
                    </span>
                    </p>
                </div>

                <div className={styles.NavbarGroup}>
                    {!isPublicView && (
                    <>
                        <div className={styles.Pickers}>
                        <AddressInput
                            onChange={(value) => handleAdressChange(value)}
                            value={orderData.address}
                            addresses={adresses}
                            className={styles.Address}
                        />
                        <DatePicker
                        /*text={t("inputs.add_day")}*/
                            onChange={(value) => handleDateChange(value)}
                            selectedDates={cartDatesISO}
                            className={styles.Date}
                        />
                        </div>
                        <button
                        className={styles.AssistantButton}
                        onClick={() => setOnboardingOpen(true)}
                        >
                        <img className={styles.Logo} src={MarikaCropped} alt="Avatar Marika" />
                        <div className={styles.AssistantButtonRect}></div>
                        </button>
                    </>
                    )}

                    {!isPublicView && (
                    <OnboardingPopup
                        display={onboardingOpen}
                        onClose={() => {
                        setOnboardingOpen(false);
                        clearData();
                        }}
                    />
                    )}

                    <div className={styles.NavbarMenu}>
                    <SearchButton />
                    <UserButton LoggedIn={loggedIn && !isPublicView} logout={logout} />
                    {!isPublicView && (
                        <CartButton
                        onClick={cartOpen ? closeCart : openCart}
                        indicatorText={indicatorText}
                        />
                    )}
                    </div>
                </div>
                </div>

                {!isPublicView && (
                <div className={styles.Tabs}>
                    {cartDatesISO.map((date) => (
                    <div
                        onClick={() => handleOrder("selectedDate", date)}
                        key={date}
                        className={
                        orderData.selectedDate === date
                            ? `${styles.selecteDate} ${styles.DateTab}`
                            : `${styles.DateTab}`
                        }
                    >
                        <p>{formatDate(date, true)}</p>
                    </div>
                    ))}
                </div>
                )}
            </>
            ) : (
            <>
                <OrderSettingsPopup
                isOpen={orderSettingsOpen}
                setOpen={setOrderSettingsOpen}
                selectedDates={cartDatesISO}
                setSelectedDates={(value) => handleDateChange(value)}
                />

                <div className={styles.NavbarMain}>
                <div className={styles.LogoWrapMobile}>
                    {!isPublicView ? (
                    <Link to={"/home"}>
                        <img className={styles.Logo} src={catermatLogo} alt="Catermat Logo" />
                    </Link>
                    ) : (
                    <img className={styles.Logo} src={catermatLogo} alt="Catermat Logo" />
                    )}
                    <p>
                    {t("navbar.for")}
                    <span>
                        {" " +
                        userData.subscribers?.find(
                            (sub) => sub.subscriber_id === cartData?.subscriber_id
                        )?.name || ""}
                    </span>
                    </p>
                </div>

                <div className={styles.NavbarMenu}>
                    {!isPublicView && (
                    <button
                        className={styles.Picker}
                        onClick={() => setOrderSettingsOpen(true)}
                    >
                        {t("navbar.date_address")}
                    </button>
                    )}
                    <SearchButton />
                    {!isPublicView && (
                    <CartButton
                        onClick={cartOpen ? closeCart : openCart}
                        indicatorText={indicatorText}
                    />
                    )}
                    <button className={styles.BurgerMenu} onClick={() => setMenuOpen(true)}>
                    <Menu />
                    </button>
                </div>
                </div>

                {!isPublicView && (
                <div className={styles.Tabs}>
                    {cartDatesISO.map((date) => (
                    <div
                        onClick={() => handleOrder("selectedDate", date)}
                        key={date}
                        className={
                        orderData.selectedDate === date
                            ? `${styles.selecteDate} ${styles.DateTab}`
                            : `${styles.DateTab}`
                        }
                    >
                        <p>{formatDate(date, true)}</p>
                    </div>
                    ))}
                </div>
                )}
            </>
            )}
        </div>
        </>
    );
};

export default Navbar;


